'use client';

import a__default, { forwardRef, useMemo } from "react";
import { useSelect } from "../../../external/downshift/dist/downshift.esm.js";
import { createPortal } from "react-dom";
import { Text } from "../../atoms/Text/Text.js";
import { FormControl } from "../../atoms/FormControl/FormControl.js";
import { CaretUp, CaretDown } from "../../../react-icons.js";
import { useId } from "../../utils/hooks/useId.js";
import { mergeRefs } from "../../utils/props/mergeRefs.js";
import { classNames } from "../../utils/styles/classNames.js";
import { parseProps } from "./utils/parseProps.js";
import { useAnnouncementForEmpty } from "./utils/useAnnouncementForEmpty.js";
import { useAnnouncementForLoading } from "./utils/useAnnouncementForLoading.js";
import { getAriaDescribedBy } from "./utils/getAriaDescribedBy.js";
import { getCurrentState } from "./utils/getCurrentState.js";
import { useFloatingUi } from "./utils/useFloatingUi.js";
import { Empty } from "./Select.Empty.js";
import { Loading } from "./Select.Loading.js";
import { Option } from "./Select.Option.js";
import { wrapper, inputBorder, input, inputLabel, listDisplay, listHeight, list } from "./Select.css.js";
function SelectComponent(props, ref) {
  const { formControlProps, inputProps, selectFieldProps } = parseProps(props);
  const {
    options,
    placeholder,
    dataQa,
    onChange,
    emptyLabel,
    isLoading,
    value
  } = selectFieldProps;
  const { disabled, error } = formControlProps;
  const autoId = useId();
  const id = inputProps.id ?? autoId;
  const descriptionId = `chalk-select-description-id-${id}`;
  const hasCaption = !!options.find((i) => !!i.caption);
  const currentState = getCurrentState({ disabled, error });
  const noOptionsProvided = options.length === 0;
  const selectedItem = useMemo(
    () => options.find((i) => i.value === value) ?? null,
    [value]
  );
  const {
    isOpen,
    openMenu,
    closeMenu,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps
  } = useSelect({
    selectedItem,
    items: options,
    itemToString: (item) => item ? item.label : "",
    onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
      if (newSelectedItem) onChange(newSelectedItem.value);
    }
  });
  useAnnouncementForEmpty(selectFieldProps);
  useAnnouncementForLoading(selectFieldProps);
  const { ref: triggerRef, ...restButtonProps } = getToggleButtonProps({
    disabled
  });
  const { ref: wrapperRef, ...restMenuProps } = getMenuProps();
  const { dropdownProps, triggerProps } = useFloatingUi({
    isOpen,
    onOpenChange(shouldOpen) {
      if (shouldOpen && !disabled) openMenu();
      closeMenu();
    },
    triggerRef: triggerRef ? mergeRefs(triggerRef, ref) : ref,
    wrapperRef
  });
  const CaretIcon = isOpen ? CaretUp : CaretDown;
  const selectedOptionLabel = selectedItem == null ? void 0 : selectedItem.label;
  const hasSelectedOption = !!selectedOptionLabel;
  const triggerTextColor = hasSelectedOption ? "onLight.text.primary" : "onLight.text.tertiary";
  return /* @__PURE__ */ a__default.createElement(
    FormControl,
    {
      ...formControlProps,
      descriptionId,
      label: {
        ...formControlProps.label,
        ...getLabelProps()
      },
      a11yAnnounceRequired: true
    },
    /* @__PURE__ */ a__default.createElement("div", { className: wrapper, ref }, /* @__PURE__ */ a__default.createElement(
      "div",
      {
        className: classNames(input, inputBorder[currentState]),
        "data-qa": dataQa,
        ...triggerProps,
        ...getAriaDescribedBy(formControlProps, descriptionId, inputProps),
        ...restButtonProps,
        "aria-disabled": !!disabled || void 0,
        "aria-invalid": !!formControlProps.error || void 0
      },
      /* @__PURE__ */ a__default.createElement(
        Text,
        {
          "aria-hidden": !hasSelectedOption && placeholder ? "true" : void 0,
          className: inputLabel,
          color: disabled ? "onLight.disabled.muted" : triggerTextColor,
          variant: "body.base"
        },
        selectedOptionLabel || placeholder
      ),
      /* @__PURE__ */ a__default.createElement(
        CaretIcon,
        {
          color: disabled ? "onLight.disabled.muted" : "onLight.icon.secondary",
          size: "sm"
        }
      )
    ), createPortal(
      /* @__PURE__ */ a__default.createElement(
        "ul",
        {
          className: classNames(
            list,
            listHeight[hasCaption ? "withCaption" : "default"],
            listDisplay[isOpen ? "default" : "hidden"]
          ),
          ...dropdownProps,
          ...restMenuProps
        },
        /* @__PURE__ */ a__default.createElement(
          Empty,
          {
            emptyLabel,
            isLoading: !!isLoading,
            noOptionsProvided
          }
        ),
        /* @__PURE__ */ a__default.createElement(Loading, { isLoading: !!isLoading }),
        options.map((option, index) => {
          const itemProps = getItemProps({
            item: option,
            index
          });
          return /* @__PURE__ */ a__default.createElement(
            Option,
            {
              isHighlighted: highlightedIndex === index,
              isSelected: (selectedItem == null ? void 0 : selectedItem.value) === option.value,
              itemProps,
              key: option.value,
              option
            }
          );
        })
      ),
      document.body
    ))
  );
}
const Select = /* @__PURE__ */ forwardRef(SelectComponent);
export {
  Select
};
